'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

/* ─── DATA ──────────────────────────────────────────────── */

const marketplaceTypes = [
  {
    icon: 'FireIcon',
    label: 'Multi-restaurant Food Marketplace',
    desc: 'Multiple restaurants under one branded app — customers browse menus, order from different kitchens, and track all deliveries in one place.',
    tags: ['Food', 'Restaurants', 'Multi-kitchen'],
    color: '#B45309',
  },
  {
    icon: 'ShoppingCartIcon',
    label: 'Multi-store Grocery Marketplace',
    desc: 'Aggregate multiple grocery stores, supermarkets, and specialty shops under one marketplace with unified search and checkout.',
    tags: ['Grocery', 'Stores', 'Multi-category'],
    color: '#15803D',
  },
  {
    icon: 'HomeModernIcon',
    label: 'Service Provider Marketplace',
    desc: 'On-demand service providers — plumbers, electricians, cleaners — all onboarded under one platform with booking, tracking, and payment.',
    tags: ['Services', 'Hyperlocal', 'On-demand'],
    color: '#0E7490',
  },
  {
    icon: 'Squares2X2Icon',
    label: 'Multi-seller Product Marketplace',
    desc: 'Build your own branded eCommerce marketplace where multiple sellers list and sell products with centralised fulfilment control.',
    tags: ['eCommerce', 'Sellers', 'Products'],
    color: '#7C3AED',
  },
  {
    icon: 'BuildingStorefrontIcon',
    label: 'Hyperlocal Multi-store Platform',
    desc: 'Local stores across categories — grocery, pharmacy, electronics — available on one super-app for same-day neighbourhood delivery.',
    tags: ['Hyperlocal', 'Multi-category', 'Same-day'],
    color: '#0459F7',
  },
];

const coreFeatures = [
  {
    num: '01',
    icon: 'UserGroupIcon',
    label: 'Vendor Onboarding & Profile Management',
    desc: 'Vendors apply via a structured onboarding flow — business registration, document upload, bank details, and category selection. Each vendor gets a verified profile with a dedicated subdomain and branding within the marketplace.',
    pills: ['Self-service registration', 'Document verification', 'Category assignment', 'Profile management', 'Subdomain per vendor'],
  },
  {
    num: '02',
    icon: 'CurrencyRupeeIcon',
    label: 'Commission & Revenue Split Engine',
    desc: 'Configure flexible commission structures per vendor, category, or order type — fixed amount, percentage of GMV, or tiered rates tied to volume. Commission is automatically deducted before payout, no manual reconciliation needed.',
    pills: ['Flat or percentage', 'Category-wise rates', 'Volume-tiered discounts', 'Auto-deduction', 'Custom per vendor'],
  },
  {
    num: '03',
    icon: 'BuildingStorefrontIcon',
    label: 'Individual Vendor Dashboards & Analytics',
    desc: 'Every vendor gets a private dashboard showing their orders, revenue, top products, customer feedback, and payout history — scoped entirely to their data. Vendors see only what belongs to them.',
    pills: ['Private portal', 'Order management', 'Revenue tracking', 'Customer insights', 'Payout history'],
  },
  {
    num: '04',
    icon: 'ShieldCheckIcon',
    label: 'Centralized Quality & Compliance Control',
    desc: 'Marketplace administrators review and approve all product listings before they go live. Low-rated vendors trigger automated escalations. Violation policies, suspensions, and reinstatements are all managed from the admin panel.',
    pills: ['Product approval flow', 'Vendor rating monitor', 'Auto-escalation', 'Suspension controls', 'Policy enforcement'],
  },
  {
    num: '05',
    icon: 'BellAlertIcon',
    label: 'Automated Payout & Settlement System',
    desc: 'Vendor payouts run on configurable cycles — daily, weekly, fortnightly, or monthly. The platform auto-deducts commission, applies applicable tax, and triggers bank transfers. Full audit trail with downloadable settlement statements.',
    pills: ['Daily to monthly cycles', 'Auto commission deduction', 'Tax calculation', 'Bank transfer automation', 'Settlement statements'],
  },
  {
    num: '06',
    icon: 'ChartBarIcon',
    label: 'Marketplace-wide Performance Analytics',
    desc: 'Aggregate intelligence across your entire marketplace — total GMV, active vendor count, best-performing categories, top-selling products, platform revenue, customer acquisition cost, and retention trends — updated in real time.',
    pills: ['GMV dashboard', 'Vendor ranking', 'Category performance', 'Platform revenue', 'Retention analytics'],
  },
];

const vendorLifecycle = [
  { icon: 'DocumentTextIcon', title: 'Apply', desc: 'Vendor submits business registration, documents, and category selection.' },
  { icon: 'ShieldCheckIcon', title: 'Verify', desc: 'Admin reviews documents, runs compliance checks, and approves the account.' },
  { icon: 'CubeIcon', title: 'Set Up', desc: 'Vendor builds catalog, sets pricing, configures delivery zones and operating hours.' },
  { icon: 'EyeIcon', title: 'Review', desc: 'Admin reviews all listings before they go live. Quality gate for the marketplace.' },
  { icon: 'BoltIcon', title: 'Go Live', desc: 'Vendor is published on the marketplace. Customers can discover, browse, and order.' },
  { icon: 'CurrencyRupeeIcon', title: 'Get Paid', desc: 'Automated payout on configured cycle. Settlement report sent automatically.' },
];

const commissionTypes = [
  { icon: 'TagIcon', label: 'Flat Per Order', desc: 'Fixed rupee amount per completed order regardless of order value.' },
  { icon: 'ChartPieIcon', label: 'Percentage of GMV', desc: 'Commission calculated as a percentage of each order\'s gross value.' },
  { icon: 'ArrowPathIcon', label: 'Category-wise Rates', desc: 'Different rates for different product or service categories within the same marketplace.' },
  { icon: 'StarIcon', label: 'Tiered Volume Discounts', desc: 'Commission rate reduces as vendor reaches higher monthly GMV milestones.' },
];

const ownerFeatures = [
  'Approve / Reject Vendor Applications',
  'Set Commission per Vendor or Category',
  'Review & Approve All Product Listings',
  'Monitor Vendor Performance & Ratings',
  'Run Marketplace-wide Promotions',
  'Manage Delivery Zones for All Vendors',
  'Handle Customer Disputes & Escalations',
  'Configure Payout Schedules & Tax Rules',
  'Consolidated Revenue & GMV Reports',
  'Role-based Admin Access Control',
  'Notification & Broadcast Messaging',
  'API & Third-party Integrations',
];

const vendorPortalFeatures = [
  'Product Catalog & Category Builder',
  'Real-time Order Dashboard',
  'Inventory & Stock Management',
  'Pricing & Discount Configuration',
  'Customer Order History & CRM',
  'Earnings & Commission Statement',
  'Payout History & Upcoming Transfers',
  'Performance Analytics by Product',
  'Customer Ratings & Reviews View',
  'Operating Hours & Zone Setup',
  'Promo & Offer Management',
  'Staff Sub-account Access',
];

const customerFeatures = [
  'Browse All Vendors in One App',
  'Filter by Category, Rating & Distance',
  'Smart Search Across All Vendor Catalogs',
  'Add Items from Multiple Vendors to Cart',
  'Live Order Tracking per Vendor',
  'In-app Chat & Order Support',
  'Loyalty Points Across Marketplace',
  'Multiple Payment Options',
  'Order History & Reorder',
  'Rate & Review Each Vendor',
  'Wishlist & Saved Items',
  'Referral & Rewards Program',
];

const settlementFeatures = [
  { icon: 'CalendarDaysIcon', label: 'Daily Payout', desc: 'Vendors receive earnings every day for completed orders.' },
  { icon: 'ArrowPathIcon', label: 'Weekly Settlement', desc: 'Consolidated weekly payout with itemised commission statement.' },
  { icon: 'DocumentTextIcon', label: 'Monthly Billing', desc: 'End-of-month invoice and bank transfer for enterprise vendors.' },
  { icon: 'AdjustmentsHorizontalIcon', label: 'Custom Cycles', desc: 'Set unique payout schedules per vendor based on contractual terms.' },
];

const additionalFeatures = [
  { icon: 'GlobeAltIcon', label: 'Vendor Storefront Pages', sub: 'Each vendor gets a branded page within your marketplace' },
  { icon: 'MegaphoneIcon', label: 'Sponsored Listings', sub: 'Vendors bid for top placement with paid promotions' },
  { icon: 'ChatBubbleLeftRightIcon', label: 'WhatsApp Vendor Alerts', sub: 'Order and payout notifications via WhatsApp' },
  { icon: 'MapPinIcon', label: 'Geo-fencing per Vendor', sub: 'Each vendor serves only their defined delivery radius' },
  { icon: 'ArrowPathIcon', label: 'Returns & Refund Engine', sub: 'Platform-level policy with vendor-specific rules' },
  { icon: 'PuzzlePieceIcon', label: 'Third-party API Integration', sub: 'Connect ERPs, payment providers, and logistics APIs' },
];

const stats = [
  { value: 'Unlimited', label: 'Vendors on One Platform', icon: 'UserGroupIcon' },
  { value: 'Auto', label: 'Commission & Payout Engine', icon: 'CurrencyRupeeIcon' },
  { value: '5 Types', label: 'Marketplace Verticals', icon: 'Squares2X2Icon' },
  { value: '21 Days', label: 'Go-Live Timeline', icon: 'BoltIcon' },
];

/* ─── COMPONENT ──────────────────────────────────────────── */

export default function MultiVendorContent() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (el) {
      const t = setTimeout(() => {
        el.querySelectorAll('.animate-on-scroll').forEach((item) => item.classList.add('is-visible'));
      }, 30);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pt-20">

      {/* ── HERO (rose gradient + marketplace architecture) ── */}
      <div
        ref={heroRef}
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1A0010 0%, #4C0519 40%, #BE185D 100%)' }}
      >
        <div className="absolute inset-0 grid-lines opacity-15 pointer-events-none" />
        <div className="orb absolute w-[700px] h-[500px] rounded-full opacity-15 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(244,63,94,0.6) 0%, transparent 65%)', top: '-150px', right: '-100px' }} />
        <div className="orb-2 absolute w-[400px] h-[400px] rounded-full opacity-10 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(190,24,93,0.8) 0%, transparent 70%)', bottom: '-80px', left: '-80px' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left */}
            <div>
              <Link href="/products" className="animate-on-scroll stagger-1 inline-flex items-center gap-2 text-white/50 text-sm font-medium mb-7 hover:text-white/80 transition-colors">
                <Icon name="ArrowLeftIcon" size={14} />
                Back to Products
              </Link>

              <span className="animate-on-scroll stagger-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FDA4AF]/40 bg-[#FDA4AF]/10 text-[11px] font-semibold tracking-widest text-[#FDA4AF] uppercase mb-5">
                <Icon name="Squares2X2Icon" size={11} />
                Multi-Vendor Marketplace
              </span>

              <h1 className="animate-on-scroll stagger-2 font-display text-5xl md:text-6xl font-semibold text-white leading-tight tracking-tight mb-5 mt-2">
                Your marketplace.
                <br />
                <span className="italic font-light" style={{ color: '#FDA4AF' }}>Unlimited vendors.</span>
                <br />
                <span style={{ color: '#FB7185' }}>One platform.</span>
              </h1>

              <p className="animate-on-scroll stagger-3 text-white/60 text-lg leading-relaxed mb-9 max-w-lg">
                Build a full-scale multi-vendor marketplace where restaurants, stores, or service providers operate under your brand — with centralised control, automated commissions, and real-time analytics.
              </p>

              <div className="animate-on-scroll stagger-4 flex flex-wrap gap-4 mb-8">
                <Link
                  href="/demo-booking"
                  className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-semibold text-[#1A0010] hover:opacity-90 shadow-lg transition-all hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #FDA4AF, #FB7185)' }}
                >
                  Book a Free Demo
                  <Icon name="ArrowRightIcon" size={16} />
                </Link>
                <Link href="/contact" className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-medium text-white/75 border border-white/20 hover:border-white/45 hover:text-white transition-all">
                  <Icon name="PhoneIcon" size={15} />
                  +91 9600830488
                </Link>
              </div>

              <div className="animate-on-scroll stagger-5 flex flex-wrap gap-x-6 gap-y-2">
                {['Unlimited vendors', 'Automated commissions', 'Auto-payout engine', '21-day go-live'].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 text-xs text-white/45 font-medium">
                    <Icon name="CheckCircleIcon" size={13} className="text-[#FB7185]" />
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — 3-tier marketplace architecture */}
            <div className="animate-on-scroll stagger-3">
              <div className="rounded-2xl overflow-hidden border border-white/10" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(16px)' }}>
                <div className="px-6 py-4 border-b border-white/[0.08]">
                  <p className="text-[11px] font-semibold text-white/35 uppercase tracking-widest">Marketplace Architecture</p>
                </div>

                <div className="p-5 flex flex-col gap-4">
                  {/* Tier 1: Platform Owner */}
                  <div className="rounded-xl p-4 text-center border border-[#FDA4AF]/25" style={{ background: 'rgba(190,24,93,0.2)' }}>
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Icon name="ShieldCheckIcon" size={14} className="text-[#FDA4AF]" />
                      <span className="text-xs font-bold text-[#FDA4AF] uppercase tracking-widest">You — Marketplace Owner</span>
                    </div>
                    <p className="text-white/55 text-[11px]">Commission engine · Quality control · Analytics · Payouts</p>
                  </div>

                  {/* Arrow down */}
                  <div className="flex items-center justify-center gap-2">
                    <div className="flex-1 h-px bg-white/10" />
                    <Icon name="ChevronDownIcon" size={14} className="text-white/30" />
                    <span className="text-[10px] text-white/30 font-medium">controls & enables</span>
                    <Icon name="ChevronDownIcon" size={14} className="text-white/30" />
                    <div className="flex-1 h-px bg-white/10" />
                  </div>

                  {/* Tier 2: Vendors */}
                  <div>
                    <p className="text-[10px] font-semibold text-white/30 uppercase tracking-widest text-center mb-2">Your Vendors</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { label: 'Vendor A', icon: 'BuildingStorefrontIcon', color: '#B45309' },
                        { label: 'Vendor B', icon: 'FireIcon', color: '#15803D' },
                        { label: 'Vendor C', icon: 'HomeModernIcon', color: '#0459F7' },
                      ].map((v) => (
                        <div key={v.label} className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] transition-colors">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: v.color }}>
                            <Icon name={v.icon as Parameters<typeof Icon>[0]['name']} size={15} className="text-white" />
                          </div>
                          <span className="text-[10px] font-semibold text-white/70">{v.label}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-center text-[10px] text-white/30 mt-2 font-medium">+ Unlimited more vendors</p>
                  </div>

                  {/* Arrow down */}
                  <div className="flex items-center justify-center gap-2">
                    <div className="flex-1 h-px bg-white/10" />
                    <Icon name="ChevronDownIcon" size={14} className="text-white/30" />
                    <span className="text-[10px] text-white/30 font-medium">serve</span>
                    <Icon name="ChevronDownIcon" size={14} className="text-white/30" />
                    <div className="flex-1 h-px bg-white/10" />
                  </div>

                  {/* Tier 3: Customers */}
                  <div className="rounded-xl p-4 text-center border border-white/10" style={{ background: 'rgba(255,255,255,0.04)' }}>
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Icon name="UserGroupIcon" size={14} className="text-white/60" />
                      <span className="text-xs font-bold text-white/60 uppercase tracking-widest">Your Customers</span>
                    </div>
                    <p className="text-white/40 text-[11px]">One app · All vendors · Unified checkout</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── METRICS STRIP ── */}
      <div style={{ background: 'linear-gradient(135deg, #4C0519 0%, #881337 55%, #BE185D 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {stats.map((s, i) => (
              <div key={s.label} className={`animate-on-scroll stagger-${i + 1} flex items-center gap-4 px-6 py-7`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/15">
                  <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-[#FDA4AF]" />
                </div>
                <div>
                  <div className="font-display text-2xl font-semibold text-white leading-none mb-0.5">{s.value}</div>
                  <div className="text-xs text-white/55 font-medium">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── MARKETPLACE TYPES ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(190,24,93,0.3)] bg-[rgba(190,24,93,0.07)] text-[11px] font-semibold tracking-widest text-[#BE185D] uppercase mb-4">
              What You Can Build
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Five marketplace verticals.{' '}
              <span className="italic font-light text-[#BE185D]">One platform engine.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-2xl mx-auto">Multi-Vendor powers any marketplace model — food, grocery, services, products, or hyperlocal — with the same core infrastructure.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {marketplaceTypes.map((m, i) => (
              <div
                key={m.label}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} group rounded-2xl p-7 border hover:shadow-lg transition-all duration-300 ${i === 4 ? 'lg:col-span-1 md:col-span-2 lg:col-span-1' : ''}`}
                style={{ borderColor: m.color + '20', background: i % 2 === 0 ? 'white' : '#FFF1F2' }}
              >
                <div
                  className="w-13 h-13 w-12 h-12 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform flex-shrink-0"
                  style={{ background: m.color }}
                >
                  <Icon name={m.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-white" />
                </div>
                <h3 className="font-display text-base font-semibold text-[#0F172A] mb-2">{m.label}</h3>
                <p className="text-sm text-[#475569] leading-relaxed mb-4">{m.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {m.tags.map((t) => (
                    <span key={t} className="text-[10px] font-semibold px-2.5 py-1 rounded-full text-[#BE185D]" style={{ background: 'rgba(190,24,93,0.07)' }}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            ))}

            {/* "Build your own" card */}
            <div className="animate-on-scroll stagger-6 group rounded-2xl p-7 border border-dashed border-[rgba(190,24,93,0.3)] hover:border-[#BE185D] hover:shadow-md transition-all duration-300 bg-[#FFF1F2] flex flex-col items-center justify-center text-center">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{ background: 'rgba(190,24,93,0.1)' }}>
                <Icon name="PlusCircleIcon" size={22} className="text-[#BE185D]" />
              </div>
              <h3 className="font-display text-base font-semibold text-[#0F172A] mb-2">Custom Marketplace</h3>
              <p className="text-sm text-[#475569] leading-relaxed">Have a unique use case? The platform is fully configurable for any multi-vendor model.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── CORE 6 FEATURES ── */}
      <div className="py-24" style={{ background: '#FFF1F2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(190,24,93,0.3)] bg-[rgba(190,24,93,0.07)] text-[11px] font-semibold tracking-widest text-[#BE185D] uppercase mb-4">
              Key Features
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Six capabilities that run{' '}
              <span className="italic font-light text-[#BE185D]">your entire marketplace.</span>
            </h2>
          </div>

          <div className="flex flex-col gap-4">
            {coreFeatures.map((f, i) => (
              <div
                key={f.num}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} group grid md:grid-cols-[260px_1fr] gap-0 rounded-2xl overflow-hidden border hover:shadow-md transition-all duration-300`}
                style={{ borderColor: 'rgba(190,24,93,0.12)', background: 'white' }}
              >
                {/* Left — accent panel */}
                <div
                  className="p-7 flex flex-col justify-between"
                  style={{ background: i % 2 === 0 ? 'rgba(190,24,93,0.06)' : 'rgba(190,24,93,0.03)' }}
                >
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <span
                        className="font-mono-custom text-xs font-bold w-9 h-9 rounded-full flex items-center justify-center text-white flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #BE185D, #9F1239)' }}
                      >
                        {f.num}
                      </span>
                      <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                        style={{ background: 'rgba(190,24,93,0.1)' }}
                      >
                        <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-[#BE185D]" />
                      </div>
                    </div>
                    <h3 className="font-display text-sm font-semibold text-[#0F172A] leading-snug">{f.label}</h3>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {f.pills.map((p) => (
                      <span key={p} className="text-[10px] font-semibold px-2 py-0.5 rounded-full text-[#BE185D]" style={{ background: 'rgba(190,24,93,0.08)' }}>
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
                {/* Right — description */}
                <div className="p-7 flex items-center border-l border-[rgba(190,24,93,0.08)]">
                  <p className="text-[#475569] text-base leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── VENDOR LIFECYCLE (dark rose) ── */}
      <div className="py-24 relative overflow-hidden" style={{ background: '#1A0010' }}>
        <div className="absolute inset-0 grid-lines opacity-10 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[300px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(190,24,93,0.4) 0%, transparent 70%)', filter: 'blur(50px)' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FDA4AF]/30 bg-[#FDA4AF]/10 text-[11px] font-semibold tracking-widest text-[#FDA4AF] uppercase mb-5">
              Vendor Lifecycle
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-white tracking-tight">
              From application{' '}
              <span className="italic font-light text-[#FDA4AF]">to first payout.</span>
            </h2>
            <p className="text-white/50 text-lg mt-4 max-w-xl mx-auto">Every vendor follows a structured journey — verified, live, and earning on your marketplace.</p>
          </div>

          <div className="relative grid sm:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="hidden lg:block absolute top-[26px] left-[calc(8.33%+20px)] right-[calc(8.33%+20px)] h-px"
              style={{ background: 'linear-gradient(90deg, rgba(253,164,175,0.05), rgba(253,164,175,0.3) 15%, rgba(253,164,175,0.3) 85%, rgba(253,164,175,0.05))' }} />
            {vendorLifecycle.map((step, i) => (
              <div key={step.title} className={`animate-on-scroll stagger-${i + 2} flex flex-col items-center text-center`}>
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 z-10"
                  style={{ background: 'linear-gradient(135deg, #BE185D, #9F1239)', boxShadow: '0 0 24px rgba(190,24,93,0.45)' }}
                >
                  <Icon name={step.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-white" />
                </div>
                <div className="text-[10px] font-bold text-[#FDA4AF]/50 tracking-widest uppercase mb-1.5">Stage {i + 1}</div>
                <h3 className="font-semibold text-white text-sm mb-2">{step.title}</h3>
                <p className="text-xs text-white/45 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── COMMISSION ENGINE ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="animate-on-scroll stagger-1">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(190,24,93,0.3)] bg-[rgba(190,24,93,0.07)] text-[11px] font-semibold tracking-widest text-[#BE185D] uppercase mb-5">
                Commission Engine
              </span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-5">
                Flexible commissions,{' '}
                <span className="italic font-light text-[#BE185D]">zero manual reconciliation.</span>
              </h2>
              <p className="text-[#475569] text-lg leading-relaxed mb-8">
                Define commission rules the way your business model demands — a flat fee per order, a percentage of GMV, category-specific rates, or volume-based tiers that reward high-performing vendors. The platform auto-deducts the correct amount at every settlement cycle.
              </p>
              <div className="flex flex-col gap-3">
                {[
                  'Commission auto-calculated at order completion',
                  'Deducted before vendor payout — no chasing payments',
                  'Different rates configurable per vendor or category',
                  'Override capability for special partnership terms',
                  'Full audit log of every commission deduction',
                  'Exportable reports for GST and tax filing',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #BE185D, #9F1239)' }}>
                      <Icon name="CheckIcon" size={11} className="text-white" />
                    </div>
                    <span className="text-sm text-[#475569]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="animate-on-scroll stagger-2 flex flex-col gap-4">
              <h3 className="font-semibold text-sm uppercase tracking-widest text-[#64748B] mb-2">Commission Types</h3>
              {commissionTypes.map((c, i) => (
                <div key={c.label} className={`animate-on-scroll stagger-${i + 2} flex gap-4 p-5 rounded-2xl border border-[rgba(190,24,93,0.1)] hover:border-[rgba(190,24,93,0.3)] hover:shadow-sm transition-all group`}
                  style={{ background: i % 2 === 0 ? '#FFF1F2' : 'white' }}>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ background: 'rgba(190,24,93,0.1)' }}>
                    <Icon name={c.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-[#BE185D]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] mb-1">{c.label}</p>
                    <p className="text-sm text-[#475569] leading-relaxed">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── 3-TIER SECTIONS (Owner / Vendor / Customer) ── */}
      <div className="py-24" style={{ background: '#FFF1F2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(190,24,93,0.3)] bg-[rgba(190,24,93,0.07)] text-[11px] font-semibold tracking-widest text-[#BE185D] uppercase mb-4">
              Platform Roles
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Three roles. Purpose-built{' '}
              <span className="italic font-light text-[#BE185D]">for each one.</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Marketplace Owner */}
            <div className="animate-on-scroll stagger-2 rounded-2xl overflow-hidden border border-[rgba(190,24,93,0.15)] shadow-sm hover:shadow-md transition-shadow">
              <div className="px-7 py-6" style={{ background: 'linear-gradient(135deg, #4C0519, #BE185D)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon name="ShieldCheckIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Marketplace Owner</h3>
                    <p className="text-white/60 text-xs">Admin — Full Control</p>
                  </div>
                </div>
              </div>
              <div className="p-5 grid sm:grid-cols-2 gap-2 bg-white">
                {ownerFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-[#FFF1F2] border border-[rgba(190,24,93,0.07)]">
                    <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(190,24,93,0.15)' }}>
                      <Icon name="CheckIcon" size={9} className="text-[#BE185D]" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Vendor Portal */}
            <div className="animate-on-scroll stagger-3 rounded-2xl overflow-hidden border border-[rgba(15,23,42,0.07)] shadow-sm hover:shadow-md transition-shadow">
              <div className="px-7 py-6 bg-[#0F172A]">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                    <Icon name="BuildingStorefrontIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Vendor Portal</h3>
                    <p className="text-white/50 text-xs">Web Dashboard — Per Vendor</p>
                  </div>
                </div>
              </div>
              <div className="p-5 grid sm:grid-cols-2 gap-2 bg-[#F5F5F5]">
                {vendorPortalFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-white border border-[rgba(15,23,42,0.06)]">
                    <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center bg-[#0F172A]">
                      <Icon name="CheckIcon" size={9} className="text-white" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Customer App */}
            <div className="animate-on-scroll stagger-4 rounded-2xl overflow-hidden border border-[rgba(190,24,93,0.15)] shadow-sm hover:shadow-md transition-shadow">
              <div className="px-7 py-6" style={{ background: 'linear-gradient(135deg, #881337, #BE185D)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon name="DevicePhoneMobileIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Customer App</h3>
                    <p className="text-white/60 text-xs">iOS & Android</p>
                  </div>
                </div>
              </div>
              <div className="p-5 grid sm:grid-cols-2 gap-2 bg-[#FFF1F2]">
                {customerFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-white border border-[rgba(190,24,93,0.07)]">
                    <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(190,24,93,0.12)' }}>
                      <Icon name="CheckIcon" size={9} className="text-[#BE185D]" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── SETTLEMENT AUTOMATION ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(190,24,93,0.3)] bg-[rgba(190,24,93,0.07)] text-[11px] font-semibold tracking-widest text-[#BE185D] uppercase mb-4">
              Automated Payouts
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Pay every vendor on time.{' '}
              <span className="italic font-light text-[#BE185D]">Automatically.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-xl mx-auto">Configure payout frequency per vendor, auto-deduct commissions, calculate applicable tax, and trigger bank transfers — all without manual intervention.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            {settlementFeatures.map((s, i) => (
              <div key={s.label} className={`animate-on-scroll stagger-${i + 2} group rounded-2xl p-6 border border-[rgba(190,24,93,0.1)] hover:border-[rgba(190,24,93,0.3)] hover:shadow-md transition-all duration-300 ${i % 2 === 0 ? 'bg-[#FFF1F2]' : 'bg-white'}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{ background: 'rgba(190,24,93,0.1)' }}>
                  <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#BE185D]" />
                </div>
                <h3 className="font-semibold text-[#0F172A] mb-2">{s.label}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="animate-on-scroll stagger-4 rounded-2xl p-7 border border-[rgba(190,24,93,0.15)]" style={{ background: 'linear-gradient(135deg, rgba(190,24,93,0.05), rgba(159,18,57,0.02))' }}>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              {[
                { icon: 'DocumentTextIcon', title: 'Settlement Statements', desc: 'Auto-generated PDF statements sent to vendors at every payout cycle.' },
                { icon: 'ShieldCheckIcon', title: 'Full Audit Trail', desc: 'Every commission deduction and bank transfer is logged with timestamps.' },
                { icon: 'ChartBarIcon', title: 'Payout Reconciliation', desc: 'Cross-reference payouts with orders and commissions from your admin dashboard.' },
              ].map((item) => (
                <div key={item.title} className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ background: 'linear-gradient(135deg, #BE185D, #9F1239)' }}>
                    <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-white" />
                  </div>
                  <p className="font-semibold text-[#0F172A] mb-1">{item.title}</p>
                  <p className="text-sm text-[#475569]">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── ADDITIONAL FEATURES ── */}
      <div className="py-24" style={{ background: '#FFF1F2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(190,24,93,0.3)] bg-[rgba(190,24,93,0.07)] text-[11px] font-semibold tracking-widest text-[#BE185D] uppercase mb-4">
              More Capabilities
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Everything else to scale{' '}
              <span className="italic font-light text-[#BE185D]">your marketplace.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {additionalFeatures.map((f, i) => (
              <div
                key={f.label}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} flex items-center gap-4 p-6 rounded-2xl border border-[rgba(190,24,93,0.1)] bg-white hover:border-[rgba(190,24,93,0.3)] hover:shadow-md transition-all duration-300 group`}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ background: 'rgba(190,24,93,0.1)' }}>
                  <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#BE185D]" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-[#0F172A]">{f.label}</p>
                  <p className="text-xs text-[#64748B] mt-0.5">{f.sub}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="animate-on-scroll stagger-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            {['Unlimited Vendors', 'Auto Commission Engine', 'Centralized Control', 'White-label Ready'].map((item) => (
              <div key={item} className="flex items-center gap-2 p-3 bg-white rounded-xl border border-[rgba(190,24,93,0.1)]">
                <Icon name="CheckBadgeIcon" size={16} className="text-[#BE185D] flex-shrink-0" />
                <span className="text-xs font-medium text-[#475569]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="animate-on-scroll stagger-1 rounded-3xl p-12 md:p-16 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #1A0010 0%, #4C0519 35%, #BE185D 70%, #F43F5E 100%)' }}
          >
            <div className="absolute inset-0 grid-lines opacity-15 pointer-events-none" />
            <div className="absolute top-0 right-0 w-72 h-72 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(253,164,175,0.2) 0%, transparent 70%)', transform: 'translate(50px,-50px)' }} />

            <span className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FDA4AF]/30 bg-[#FDA4AF]/10 text-[11px] font-semibold tracking-widest text-[#FDA4AF] uppercase mb-6">
              <span className="relative flex h-1.5 w-1.5">
                <span className="ping-slow absolute inline-flex h-full w-full rounded-full bg-[#FDA4AF] opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#FDA4AF]" />
              </span>
              Ready to Build Your Marketplace?
            </span>

            <h2 className="relative font-display text-4xl md:text-5xl font-semibold text-white tracking-tight mb-4">
              Your branded marketplace,{' '}
              <span className="italic font-light text-[#FDA4AF]">live in 21 days.</span>
            </h2>

            <p className="relative text-white/60 text-lg mb-5 max-w-xl mx-auto leading-relaxed">
              For demo or more information
            </p>

            <div className="relative flex flex-wrap justify-center gap-x-5 gap-y-2 mb-10 text-sm">
              <a href="tel:+919600830488" className="inline-flex items-center gap-2 text-white/65 font-medium hover:text-white transition-colors">
                <Icon name="PhoneIcon" size={14} />
                +91 9600830488
              </a>
              <span className="text-white/25 hidden sm:inline">|</span>
              <a href="mailto:contact@movio.in" className="inline-flex items-center gap-2 text-white/65 font-medium hover:text-white transition-colors">
                <Icon name="EnvelopeIcon" size={14} />
                contact@movio.in
              </a>
              <span className="text-white/25 hidden sm:inline">|</span>
              <span className="inline-flex items-center gap-2 text-white/65 font-medium">
                <Icon name="GlobeAltIcon" size={14} />
                www.movio.in
              </span>
            </div>

            <div className="relative flex flex-wrap justify-center gap-4">
              <Link
                href="/demo-booking"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold text-[#1A0010] hover:opacity-90 shadow-lg transition-all hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #FDA4AF, #FB7185)' }}
              >
                Book Free Demo
                <Icon name="ArrowRightIcon" size={18} />
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-medium text-white border-2 border-white/30 hover:border-white/60 hover:bg-white/10 transition-all duration-200"
              >
                <Icon name="PhoneIcon" size={16} />
                Talk to Sales
              </Link>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
